import { useNavigate } from 'react-router-dom';

export default function FreeSection() {
  const navigate = useNavigate();

  const freeResources = [
    {
      title: 'Fractions Made Easy',
      type: 'Free Lesson',
      duration: '20 min',
      level: 'Grade 3–5',
      topic: 'Beginner',
      thumbnail: 'https://public.readdy.ai/ai/img_res/79504dc806c8cf9d1677c38561253c36.jpg',
      icon: 'ri-pie-chart-2-line',
    },
    {
      title: 'Introduction to Algebra',
      type: 'Free Lesson',
      duration: '25 min',
      level: 'Grade 6–8',
      topic: 'Intermediate',
      thumbnail: 'https://readdy.ai/api/search-image?query=Educational%20mathematics%20algebra%20illustration%20showing%20variables%20equations%20and%20expressions%20on%20a%20clean%20white%20background%20with%20teal%20and%20cyan%20accents%20modern%20minimal%20style&width=400&height=300&seq=free-video-001&orientation=landscape',
      icon: 'ri-function-line',
    },
    {
      title: 'SAT Math Strategies',
      type: 'Free Lesson',
      duration: '35 min',
      level: 'Test Prep',
      topic: 'Advanced',
      thumbnail: 'https://readdy.ai/api/search-image?query=Educational%20SAT%20test%20preparation%20mathematics%20strategy%20illustration%20showing%20problem%20solving%20techniques%20on%20a%20clean%20white%20background%20with%20amber%20and%20gold%20accents%20modern%20minimal%20style&width=400&height=300&seq=free-video-003&orientation=landscape',
      icon: 'ri-medal-line',
    },
    {
      title: 'Geometry Basics',
      type: 'Free Lesson',
      duration: '25 min',
      level: 'Grade 7–9',
      topic: 'Intermediate',
      thumbnail: 'https://readdy.ai/api/search-image?query=Educational%20mathematics%20geometry%20illustration%20showing%20triangles%20circles%20angles%20and%20shapes%20on%20a%20clean%20white%20background%20with%20emerald%20and%20green%20accents%20modern%20minimal%20style&width=400&height=300&seq=free-worksheet-001&orientation=landscape',
      icon: 'ri-shape-line',
    },
    {
      title: 'Linear Equations & Graphing',
      type: 'Free Lesson',
      duration: '30 min',
      level: 'Grade 8–9',
      topic: 'Intermediate',
      thumbnail: 'https://readdy.ai/api/search-image?query=Educational%20mathematics%20linear%20equations%20graphing%20illustration%20showing%20coordinate%20plane%20slope%20lines%20on%20a%20clean%20white%20background%20with%20rose%20and%20pink%20accents%20modern%20minimal%20style&width=400&height=300&seq=free-worksheet-002&orientation=landscape',
      icon: 'ri-line-chart-line',
    },
    {
      title: 'Quadratic Equations',
      type: 'Free Lesson',
      duration: '30 min',
      level: 'Grade 9–10',
      topic: 'Advanced',
      thumbnail: 'https://readdy.ai/api/search-image?query=Educational%20mathematics%20quadratic%20equations%20parabola%20illustration%20showing%20graphs%20and%20formulas%20on%20a%20clean%20white%20background%20with%20violet%20and%20purple%20accents%20modern%20minimal%20style&width=400&height=300&seq=free-worksheet-003&orientation=landscape',
      icon: 'ri-bar-chart-grouped-line',
    },
  ];

  const difficultyColor = (d: string) =>
    d === 'Beginner'
      ? 'bg-green-100 text-green-700'
      : d === 'Intermediate'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-rose-100 text-rose-700';

  return (
    <section id="free-section" className="py-24 px-6 bg-gradient-to-br from-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 bg-teal-100 px-6 py-3 rounded-full mb-6">
            <i className="ri-gift-line text-2xl text-teal-600"></i>
            <span className="text-lg font-semibold text-teal-700">No Credit Card Required</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
            Free Lessons & Practice
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Experience Mr. Augustine's teaching style with fully interactive lessons — read the concept, study key points, then test yourself with instant feedback. Completely free.
          </p>
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {freeResources.map((resource, index) => (
            <div
              key={index}
              onClick={() => navigate('/free-lessons')}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group cursor-pointer"
            >
              {/* Thumbnail */}
              <div className="relative overflow-hidden h-52">
                <img
                  src={resource.thumbnail}
                  alt={resource.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-all duration-300"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/90 group-hover:bg-white transition-all duration-300 group-hover:scale-110">
                    <i className={`${resource.icon} text-3xl text-teal-600`}></i>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap">
                  {resource.type}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors duration-300">
                    {resource.title}
                  </h3>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${difficultyColor(resource.topic)}`}>
                    {resource.topic}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500 mb-5">
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-teal-500"></i>
                    <span>{resource.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-graduation-cap-line text-teal-500"></i>
                    <span>{resource.level}</span>
                  </div>
                </div>
                <button className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-600 hover:opacity-90 text-white font-semibold rounded-xl transition-all duration-300 whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-play-circle-line text-lg"></i>
                  Start Free Lesson
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Box */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-3xl p-12 text-center text-white">
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            Love What You See?
          </h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto leading-relaxed">
            Get unlimited access to over 1,500 video lessons, 3,000+ worksheets, full Regents exam prep, and comprehensive digital textbooks with a membership.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/free-lessons')}
              className="px-10 py-5 bg-white/20 border-2 border-white text-white font-bold text-lg rounded-xl hover:bg-white/30 transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer"
            >
              View All Free Lessons
            </button>
            <button
              onClick={() => {
                const element = document.getElementById('membership-tiers');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-10 py-5 bg-white text-teal-600 font-bold text-lg rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer"
            >
              Unlock Full Access
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
